import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { DesignV2Scope, Disclosure, ListRow } from "../../../design-v2";
import { StackHeader } from "../../../components/product";
import { CURRENT_ANALYSIS } from "../api/insightsApi";
import { formatDateRange } from "../domain/insightsPresentation";
import {
  HOW_IT_WORKS_TITLE,
  HUB_HISTORICAL_NOTE,
  OTHER_EXERCISES_TITLE,
  OTHER_REASON_NOTES,
  OVERVIEW_GROUP_ORDER,
  EP_STATE,
  exerciseDetailPath,
  formatHistoricalRow,
  formatOverviewContext,
  formatOverviewSummary,
  formatSessionCount,
} from "../domain/exercisePerformancePresentation";
import {
  InsightsSection,
  ChevronRightIcon,
  NoActiveCycleState,
  NoHistoryState,
  SectionError,
  SectionLoading,
  TimeframeSelector,
} from "../components/InsightsChrome";
import { HowItWorksSheet, StateDistributionBar, StateGroup } from "../components/ExercisePerformanceParts";
import { insightsPath, useExercisePerformance, useTimeframeParam } from "./useInsightsData";

/*
 * Exercise Performance screen (EP blueprint §6.4). Display only: the states,
 * counts, order and sentences are the API's (EP-5); nothing is recomputed.
 * Current Analysis shows the state groups; historical timeframes show the
 * factual list of logged exercises (OC-1), never a historical state.
 */

const LOCKED = "EXERCISE_PERFORMANCE_LOCKED";

function OtherExercises({ rows, timeframe }) {
  if (!rows?.length) return null;
  return (
    <Disclosure className="lz-insights-ep-group" title={<span data-testid="ep-other-title">{OTHER_EXERCISES_TITLE}</span>}>
      <div className="lz-insights-ep-rows" data-testid="ep-other-exercises">
        {rows.map((row) => (
          <ListRow
            key={row.exerciseId}
            as={Link}
            to={insightsPath(exerciseDetailPath(row.exerciseId), timeframe)}
            className="lz-insights-ep-row"
            title={row.name}
            description={formatSessionCount(row.sessionCount)}
            meta={OTHER_REASON_NOTES[row.reason] || null}
            trailing={<ChevronRightIcon />}
            data-testid={`ep-other-${row.exerciseId}`}
          />
        ))}
      </div>
    </Disclosure>
  );
}

function CurrentAnalysisOverview({ data, timeframe }) {
  const [helpOpen, setHelpOpen] = useState(false);
  const dataBuilding = Boolean(data.summary?.dataBuilding);
  const groups = OVERVIEW_GROUP_ORDER
    .map((state) => ({ state, rows: (data.exercises || []).filter((row) => row.state === state) }))
    .filter((group) => group.rows.length > 0);

  return (
    <>
      <InsightsSection
        title="Current Analysis"
        subtitle={formatOverviewContext(data.context)}
        headingLevel="h2"
        testId="ep-overview-summary"
      >
        <p className="lz-insights-ep-line" data-testid="ep-summary-line">{formatOverviewSummary(data.summary)}</p>
        {!dataBuilding && data.summary?.stateCounts ? <StateDistributionBar stateCounts={data.summary.stateCounts} /> : null}
      </InsightsSection>

      <div className="lz-insights-ep-groups" data-testid="ep-groups">
        {groups.map((group) => (
          <StateGroup
            key={group.state}
            state={group.state}
            rows={group.rows}
            timeframe={timeframe}
            // NOT ENOUGH DATA collapsed, except while every exercise is still building data.
            defaultOpen={group.state !== EP_STATE.NOT_ENOUGH_DATA || dataBuilding}
          />
        ))}
        <OtherExercises rows={data.otherExercises} timeframe={timeframe} />
      </div>

      <ListRow
        className="lz-insights-ep-help"
        title={HOW_IT_WORKS_TITLE}
        onClick={() => setHelpOpen(true)}
        trailing={<ChevronRightIcon />}
        data-testid="ep-how-it-works-row"
      />
      <HowItWorksSheet open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
}

function HistoricalOverview({ data, timeframe }) {
  const range = formatDateRange(data.range?.startDate, data.range?.endDate);
  return (
    <InsightsSection title="Logged exercises" subtitle={range || null} testId="ep-historical">
      <div className="lz-insights-ep-rows">
        {data.exercises.map((row) => (
          <ListRow
            key={row.exerciseId}
            as={Link}
            to={insightsPath(exerciseDetailPath(row.exerciseId), timeframe)}
            className="lz-insights-ep-row"
            title={row.name}
            description={formatHistoricalRow(row)}
            trailing={<ChevronRightIcon />}
            data-testid={`ep-history-${row.exerciseId}`}
          />
        ))}
      </div>
      <p className="lz-insights-ep-note" data-testid="ep-historical-note">{HUB_HISTORICAL_NOTE}</p>
    </InsightsSection>
  );
}

export default function ExercisePerformanceScreen() {
  const navigate = useNavigate();
  const [timeframe, setTimeframe] = useTimeframeParam();
  const state = useExercisePerformance(timeframe);
  const isCurrent = timeframe === CURRENT_ANALYSIS;

  // Locked (canon §8.1): the feature is hidden; the route returns to the Hub.
  if (state.status === "success" && state.data?.unavailableReason === LOCKED) {
    return <Navigate to={insightsPath("/progress", timeframe)} replace />;
  }

  let body;
  if (state.status === "loading") body = <SectionLoading label="Loading exercise performance" />;
  else if (state.status === "error") body = <SectionError what="exercise performance" onRetry={state.retry} />;
  else if (state.data?.available === false) body = <NoActiveCycleState onBrowseHistory={() => setTimeframe("3M")} />;
  else if (!isCurrent && !(state.data?.exercises || []).length) body = <NoHistoryState timeframe={timeframe} />;
  else if (isCurrent) body = <CurrentAnalysisOverview data={state.data} timeframe={timeframe} />;
  else body = <HistoricalOverview data={state.data} timeframe={timeframe} />;

  return (
    <DesignV2Scope className="lz-insights lz-insights--detail" data-testid="insights-exercise-performance">
      <StackHeader
        title="Exercise Performance"
        backLabel="Back to Insights"
        onBack={() => navigate(insightsPath("/progress", timeframe))}
      />
      <main className="lz-insights__content">
        <TimeframeSelector value={timeframe} onChange={setTimeframe} />
        {body}
      </main>
    </DesignV2Scope>
  );
}
